import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Modal, Spin, Tabs, message } from 'antd';
import { useParams } from 'react-router-dom';
import * as GoodsApi from '../../api/Goods';
import { useGoodsDetail } from './useGoodsDetail';
import { ProductHeader } from './ProductHeader';
import { ProductSummary } from './ProductSummary';
import { ProductEditDrawer } from './ProductEditDrawer';
import { OverviewTab } from './tabs/OverviewTab';
import { MarketplacesTab } from './tabs/MarketplacesTab';
import { StocksTab } from './tabs/StocksTab';
import { ReviewsTab } from './tabs/ReviewsTab';
import { QuestionsTab } from './tabs/QuestionsTab';

const GoodsDetailPage = () => {
  const { article: rawArticle } = useParams<{ article: string }>();
  const article = rawArticle ? decodeURIComponent(rawArticle) : '';
  const navigate = useNavigate();
  const detail = useGoodsDetail(article);
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editFocusCombo, setEditFocusCombo] = useState(false);
  const [bindAuthId, setBindAuthId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (detail.pending) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={detail.error || 'Товар не найден'} />
        <div style={{ marginTop: 12 }}>
          <Link to="/settings/goods">К списку товаров</Link>
        </div>
      </div>
    );
  }

  const data = detail.data;

  const openEdit = (combo = false) => {
    setEditFocusCombo(combo);
    setEditOpen(true);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Удалить товар?',
      content: `Товар «${data.article}» будет помечен как удалённый.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        setDeleting(true);
        try {
          await GoodsApi.remove(data.article);
          message.success('Товар удалён');
          navigate('/settings/goods');
        } catch (e: any) {
          message.error(e?.message || 'Не удалось удалить товар');
          throw e;
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleRestore = async () => {
    setDeleting(true);
    try {
      const next = await GoodsApi.restore(data.article);
      detail.setData(next);
      message.success('Товар восстановлен');
      void detail.reloadSide();
    } catch (e: any) {
      message.error(e?.message || 'Не удалось восстановить товар');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        background: '#f5f5f5',
        margin: '0 -12px -24px',
        padding: '0 12px 32px',
        minHeight: '100%',
      }}
    >
      <ProductHeader
        data={data}
        mpLinked={detail.mpLinked}
        mpTotal={detail.mpTotal}
        mpArchived={detail.mpRows.filter((r) => r.product?.is_archived).length}
        sideLoading={detail.sidePending}
        onEdit={() => openEdit(false)}
        onEditBasics={() => openEdit(false)}
        onDelete={handleDelete}
        onRestore={handleRestore}
        deleting={deleting}
      />

      <ProductSummary
        loading={detail.sidePending}
        reviewsLoading={detail.reviewsCountPending}
        questionsLoading={detail.questionsPending}
        stockAvailable={detail.stockAvailable}
        mpLinked={detail.mpLinked}
        mpTotal={detail.mpTotal}
        reviewsNew={detail.reviewsNew}
        questionsNew={detail.questionsNew}
        onGo={setTab}
      />

      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '8px 16px 20px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'overview',
              label: 'Обзор',
              children: (
                <OverviewTab
                  data={data}
                  mpRows={detail.mpRows}
                  usedInCombos={detail.usedInCombos}
                  stockRows={detail.stockRows}
                  questionsNew={detail.questionsNew}
                  questions={detail.questions}
                  sideLoading={detail.sidePending}
                  questionsLoading={detail.questionsPending}
                  onEdit={() => openEdit(false)}
                  onEditCombo={() => openEdit(true)}
                  onGoTab={setTab}
                  onBind={(authId) => {
                    setBindAuthId(authId);
                    setTab('marketplaces');
                  }}
                />
              ),
            },
            {
              key: 'marketplaces',
              label: 'Маркетплейсы',
              children: (
                <MarketplacesTab
                  article={data.article}
                  rows={detail.mpRows}
                  pending={detail.sidePending}
                  onChanged={() => void detail.refreshMp()}
                  openAuthId={bindAuthId}
                  onPickerClosed={() => setBindAuthId(null)}
                />
              ),
            },
            {
              key: 'stocks',
              label: 'Остатки',
              children: (
                <StocksTab
                  article={data.article}
                  rows={detail.stockRows}
                  isCombo={!!data.is_combo}
                  fbsEnabled={!!data.product_remnants?.show_for_distribution}
                  pending={detail.sidePending}
                  onRecalculated={() => void detail.reloadSide()}
                  onEditFbs={() => openEdit(false)}
                  onEditCombo={() => openEdit(true)}
                />
              ),
            },
            {
              key: 'reviews',
              label: (
                <span>
                  Отзывы{' '}
                  {!detail.reviewsCountPending && (
                    <Badge
                      count={detail.reviewsNew}
                      overflowCount={999}
                    />
                  )}
                </span>
              ),
              children: (
                <ReviewsTab
                  article={data.article}
                  items={detail.reviews}
                  total={detail.reviewsTotal}
                  pending={detail.reviewsPending}
                />
              ),
            },
            {
              key: 'questions',
              label: (
                <span>
                  Вопросы{' '}
                  {!detail.questionsPending && (
                    <Badge count={detail.questionsNew} overflowCount={99} />
                  )}
                </span>
              ),
              children: (
                <QuestionsTab
                  article={data.article}
                  items={detail.questions}
                  total={detail.questionsTotal}
                  pending={detail.questionsPending}
                />
              ),
            },
          ]}
        />
      </div>

      <ProductEditDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        data={data}
        brands={detail.brands}
        focusCombo={editFocusCombo}
        onSaved={(next) => {
          detail.setData(next);
          void detail.reloadSide();
        }}
      />
    </div>
  );
};

export default GoodsDetailPage;
